export const SAH_DELIVERY_LADDER = [
  'MISSING',
  'SUSPENDED',
  'DRAFT_ONLY',
  'AWAITING_ACKNOWLEDGEMENT',
  'SERVICE_INCLUSIONS_MISSING',
  'SERVICE_AGREEMENT_MISSING',
  'SERVICE_AGREEMENT_STALE',
  'CARE_CONSENT_MISSING',
  'BUDGET_MISSING',
  'HCP_OPENING_MISSING',
  'DELIVERY_READY',
] as const;

export type SahDeliveryStatus = (typeof SAH_DELIVERY_LADDER)[number];

export const SAH_DELIVERY_STATUS_LABELS: Record<SahDeliveryStatus, string> = {
  MISSING: 'No SAH care plan',
  SUSPENDED: 'Care plan suspended',
  DRAFT_ONLY: 'No active/agreed SAH plan version',
  AWAITING_ACKNOWLEDGEMENT: 'Provider-required plan review evidence missing',
  SERVICE_INCLUSIONS_MISSING: 'Care plan has no typed service inclusions',
  SERVICE_AGREEMENT_MISSING: 'Service agreement missing',
  SERVICE_AGREEMENT_STALE: 'Service agreement no longer matches care plan',
  CARE_CONSENT_MISSING: 'General care consent missing',
  BUDGET_MISSING: 'Current-quarter budget missing',
  HCP_OPENING_MISSING: 'HCP transition opening not recorded',
  DELIVERY_READY: 'Funding & claims ready',
};

const SAH_DELIVERY_STATUS_SET: ReadonlySet<string> = new Set(SAH_DELIVERY_LADDER);

export function isSahDeliveryStatus(
  status: string | null | undefined,
): status is SahDeliveryStatus {
  return typeof status === 'string' && SAH_DELIVERY_STATUS_SET.has(status);
}

export function isDeliveryReady(status: string | null | undefined): boolean {
  return status === 'DELIVERY_READY';
}

export function isBlockingDeliveryStatus(status: string | null | undefined): boolean {
  return !isDeliveryReady(status);
}

export function sahDeliveryStatusLabel(status: string | null | undefined): string {
  if (!isSahDeliveryStatus(status)) return 'Unknown funding & claims status';
  return SAH_DELIVERY_STATUS_LABELS[status];
}

export const SAH_READINESS_CHECK_CODES = [
  {
    code: 'ACTIVE_SUPPORT_AT_HOME_PLAN',
    label: 'Active Support at Home plan',
    detail: 'No active Support at Home plan.',
    advisory: false,
  },
  {
    code: 'ACKNOWLEDGED_CARE_PLAN',
    label: 'Provider-required plan review recorded',
    detail: 'The provider-required plan review evidence has not been recorded.',
    advisory: false,
  },
  {
    code: 'DATE_VALID_SERVICE_INCLUSIONS',
    label: 'Date-valid service inclusions',
    detail: 'No date-valid typed service inclusions.',
    advisory: false,
  },
  {
    code: 'SIGNED_SERVICE_AGREEMENT',
    label: 'Current service agreement entered',
    detail: 'No current entered service agreement is recorded.',
    advisory: false,
  },
  {
    code: 'GENERAL_CARE_CONSENT',
    label: 'General care consent',
    detail: 'General care consent not recorded.',
    advisory: false,
  },
  {
    code: 'CURRENT_QUARTER_BUDGET',
    label: 'Current-quarter budget',
    detail: 'Current-quarter budget not provisioned - provision it before financial posting.',
    advisory: false,
  },
  {
    code: 'HCP_OPENING_RECORDED',
    label: 'HCP transition opening recorded',
    detail: 'HCP transition opening not recorded.',
    advisory: false,
  },
  {
    code: 'SAH_MEANS_BASIS',
    label: 'SAH means basis',
    detail:
      "Means assessment not captured - set the client's means tier before contribution posting.",
    advisory: true,
  },
] as const;

export type SahReadinessCheckCode = (typeof SAH_READINESS_CHECK_CODES)[number]['code'];
export type SahReadinessCheckMetadata = {
  code: SahReadinessCheckCode;
  label: string;
  detail?: string;
  advisory: boolean;
};
